import { useCallback } from 'react';
import type { RevisionReasonCategory } from '@/types/budget.types';
import type { RevisionReasonCategoryConfig } from '@/types/resource-budget-settings.types';
import { useBudgetPermissions } from '@/hooks/use-budget-permissions';
import {
  createRevisionReasonCategory,
  updateRevisionReasonCategory,
  reorderRevisionReasonCategories,
  deleteRevisionReasonCategory,
} from '@/lib/data-service';

/**
 * Hook for managing revision reason categories.
 *
 * Derives its state from `useBudgetPermissions` so that all mutations
 * (add / toggle / reorder) automatically flow through the single source
 * of truth for org-level budget settings.
 *
 * @example
 * ```tsx
 * const { enabledCategories, allCategories, addCategory, toggleCategory, getCategoryLabel } =
 *   useRevisionCategories();
 *
 * // Populate a PM-facing dropdown
 * <Select>
 *   {enabledCategories.map(cat => (
 *     <SelectItem key={cat.id} value={cat.id}>{cat.label}</SelectItem>
 *   ))}
 * </Select>
 * ```
 */
export function useRevisionCategories() {
  const { settings, updateRevisionSettings } = useBudgetPermissions();
  const { revisionSettings } = settings;
  const { revisionReasonCategories } = revisionSettings;

  // ---------------------------------------------------------------------------
  // Derived lists
  // ---------------------------------------------------------------------------

  /**
   * All categories (enabled + disabled), sorted by displayOrder.
   * Used by the Settings manager so admins can see and re-enable hidden categories.
   */
  const allCategories: RevisionReasonCategoryConfig[] = [...revisionReasonCategories].sort(
    (a, b) => a.displayOrder - b.displayOrder,
  );

  /**
   * Only enabled categories sorted by displayOrder.
   * Used to populate PM-facing dropdowns when creating a revision.
   *
   * Explicitly excludes 'resource-budget-cascade' — this is a system-only
   * category that is auto-assigned when a Resource Budget cascade creates a
   * Cost or Sales revision. It must never appear in any user-facing dropdown.
   */
  const enabledCategories: RevisionReasonCategoryConfig[] = allCategories.filter(
    (cat) => cat.isEnabled && cat.id !== 'resource-budget-cascade',
  );

  // ---------------------------------------------------------------------------
  // Mutations
  // ---------------------------------------------------------------------------

  /**
   * Add a new custom category to the end of the list.
   * The id is derived from the label (slugified) with a timestamp suffix to
   * guarantee uniqueness even if two orgs create identically-named categories.
   *
   * @param label - Display name for the new category (max 50 chars)
   */
  const addCategory = useCallback(
    (label: string) => {
      const trimmed = label.trim();
      if (!trimmed) return;

      // Prevent exact duplicate labels (case-insensitive)
      const alreadyExists = revisionReasonCategories.some(
        (cat) => cat.label.toLowerCase() === trimmed.toLowerCase(),
      );
      if (alreadyExists) return;

      const slug = trimmed
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');

      const displayOrder =
        revisionReasonCategories.length > 0
          ? Math.max(...revisionReasonCategories.map((c) => c.displayOrder)) + 1
          : 1;

      const newCategory: RevisionReasonCategoryConfig = {
        id: `${slug}-${Date.now()}`,
        label: trimmed,
        isDefault: false,
        isEnabled: true,
        displayOrder,
        isCustom: true,
      };

      // Optimistic update
      updateRevisionSettings({
        ...revisionSettings,
        revisionReasonCategories: [...revisionReasonCategories, newCategory],
      });

      // Persist to API in background; revert on failure
      const previous = [...revisionReasonCategories];
      createRevisionReasonCategory({ label: trimmed, displayOrder }).catch(() => {
        updateRevisionSettings({
          ...revisionSettings,
          revisionReasonCategories: previous,
        });
      });
    },
    [revisionReasonCategories, revisionSettings, updateRevisionSettings],
  );

  /**
   * Toggle the `isEnabled` flag on a category by id.
   * Works for both default and custom categories.
   *
   * @param id - The id of the category to toggle
   */
  const toggleCategory = useCallback(
    (id: string) => {
      const target = revisionReasonCategories.find((cat) => cat.id === id);
      if (!target) return;

      const updated = revisionReasonCategories.map((cat) =>
        cat.id === id ? { ...cat, isEnabled: !cat.isEnabled } : cat,
      );

      // Optimistic update
      updateRevisionSettings({
        ...revisionSettings,
        revisionReasonCategories: updated,
      });

      // Persist to API in background; revert on failure
      const previous = [...revisionReasonCategories];
      const numericId = (target as RevisionReasonCategoryConfig & { numericId?: number }).numericId;
      if (numericId != null) {
        updateRevisionReasonCategory(numericId, {
          label: target.label,
          isEnabled: !target.isEnabled,
          displayOrder: target.displayOrder,
        }).catch(() => {
          updateRevisionSettings({
            ...revisionSettings,
            revisionReasonCategories: previous,
          });
        });
      }
    },
    [revisionReasonCategories, revisionSettings, updateRevisionSettings],
  );

  /**
   * Reorder categories by providing a new ordered list of ids.
   * Any id present in the existing categories but absent from `orderedIds`
   * will be appended at the end to prevent data loss.
   *
   * @param orderedIds - Full array of category ids in the desired display order
   */
  const reorderCategories = useCallback(
    (orderedIds: string[]) => {
      // Build a lookup map for O(1) access
      const lookup = new Map(revisionReasonCategories.map((cat) => [cat.id, cat]));

      // Place categories in specified order, then append any stragglers
      const reordered: RevisionReasonCategoryConfig[] = [];

      orderedIds.forEach((id, index) => {
        const cat = lookup.get(id);
        if (cat) {
          reordered.push({ ...cat, displayOrder: index + 1 });
          lookup.delete(id);
        }
      });

      // Append remaining categories not included in orderedIds
      let nextOrder = reordered.length + 1;
      lookup.forEach((cat) => {
        reordered.push({ ...cat, displayOrder: nextOrder });
        nextOrder++;
      });

      // Optimistic update
      updateRevisionSettings({
        ...revisionSettings,
        revisionReasonCategories: reordered,
      });

      // Persist to API in background; revert on failure
      const previous = [...revisionReasonCategories];
      const apiUpdates = reordered
        .map((cat) => {
          const numericId = (cat as RevisionReasonCategoryConfig & { numericId?: number }).numericId;
          return numericId != null ? { id: numericId, displayOrder: cat.displayOrder } : null;
        })
        .filter((u): u is { id: number; displayOrder: number } => u !== null);

      if (apiUpdates.length > 0) {
        reorderRevisionReasonCategories(apiUpdates).catch(() => {
          updateRevisionSettings({
            ...revisionSettings,
            revisionReasonCategories: previous,
          });
        });
      }
    },
    [revisionReasonCategories, revisionSettings, updateRevisionSettings],
  );

  /**
   * Permanently delete a custom category by id.
   * Only custom categories (isCustom: true) may be deleted.
   * Default system-seeded categories should be toggled off instead.
   *
   * @param id - The id of the custom category to remove
   */
  const deleteCategory = useCallback(
    (id: string) => {
      const target = revisionReasonCategories.find((cat) => cat.id === id);
      if (!target || !target.isCustom) return;

      const updated = revisionReasonCategories.filter((cat) => cat.id !== id);

      // Optimistic update
      updateRevisionSettings({
        ...revisionSettings,
        revisionReasonCategories: updated,
      });

      // Persist to API in background; revert on failure
      const previous = [...revisionReasonCategories];
      const numericId = (target as RevisionReasonCategoryConfig & { numericId?: number }).numericId;
      if (numericId != null) {
        deleteRevisionReasonCategory(numericId).catch(() => {
          updateRevisionSettings({
            ...revisionSettings,
            revisionReasonCategories: previous,
          });
        });
      }
    },
    [revisionReasonCategories, revisionSettings, updateRevisionSettings],
  );

  /**
   * Look up the human-readable label for a given `RevisionReasonCategory` id.
   * Falls back to the raw id string if no match is found (e.g. legacy data).
   *
   * @param id - A `RevisionReasonCategory` union value or custom category id
   * @returns The display label for the category
   */
  const getCategoryLabel = useCallback(
    (id: RevisionReasonCategory | string): string => {
      const match = revisionReasonCategories.find((cat) => cat.id === id);
      return match?.label ?? id;
    },
    [revisionReasonCategories],
  );

  // ---------------------------------------------------------------------------

  return {
    /** All categories (enabled + disabled), sorted by displayOrder */
    allCategories,
    /** Only enabled categories, sorted by displayOrder — use for PM-facing dropdowns */
    enabledCategories,
    /** Add a new custom category */
    addCategory,
    /** Toggle the enabled/disabled state of a category */
    toggleCategory,
    /** Re-sort all categories by providing a new ordered list of ids */
    reorderCategories,
    /** Permanently delete a custom category (isCustom: true only) */
    deleteCategory,
    /** Get the display label for a category id */
    getCategoryLabel,
  };
}
